import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Search, SlidersHorizontal, Grid3X3, LayoutGrid } from 'lucide-react';
import axios from 'axios';
import ProductCard from '../components/ProductCard';
import { useLanguage } from '../context/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CataloguePage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useLanguage();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [compactView, setCompactView] = useState(true);
  
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [category, setCategory] = useState(searchParams.get('category') || '');
  const [brand, setBrand] = useState('');
  const [minPrice, setMinPrice] = useState('');
  const [maxPrice, setMaxPrice] = useState('');
  const [inStock, setInStock] = useState(false);
  const [promoOnly, setPromoOnly] = useState(searchParams.get('promo') === '1');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (category) params.append('category', category);
      if (brand) params.append('brand', brand);
      if (minPrice) params.append('min_price', minPrice);
      if (maxPrice) params.append('max_price', maxPrice);
      if (inStock) params.append('in_stock', 'true');
      if (promoOnly) params.append('promo', 'true');
      params.append('sort_by', sortBy);
      params.append('sort_order', sortOrder);

      const response = await axios.get(`${API_URL}/api/products?${params.toString()}`);
      setProducts(response.data);
    } catch (error) {
      // Error shown via loading=false + null data check in UI
    } finally {
      setLoading(false);
    }
  }, [search, category, brand, minPrice, maxPrice, inStock, promoOnly, sortBy, sortOrder]);

  const fetchFilters = useCallback(async () => {
    try {
      const [catResponse, brandResponse] = await Promise.all([
        axios.get(`${API_URL}/api/products/categories`),
        axios.get(`${API_URL}/api/products/brands`)
      ]);
      setCategories(catResponse.data);
      setBrands(brandResponse.data);
    } catch (error) {
      // Error shown via loading=false + null data check in UI
    }
  }, []);

  useEffect(() => {
    fetchFilters();
  }, [fetchFilters]);

  useEffect(() => {
    fetchProducts();
    window.scrollTo(0, 0);
  }, [fetchProducts]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchProducts();
  };

  const clearFilters = () => {
    setSearch('');
    setCategory('');
    setBrand('');
    setMinPrice('');
    setMaxPrice('');
    setInStock(false);
    setPromoOnly(false);
    setSortBy('created_at');
    setSortOrder('desc');
  };

  const hasActiveFilters = search || category || brand || minPrice || maxPrice || inStock || promoOnly;

  return (
    <div className="min-h-screen bg-main pt-24 pb-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-6">
          <p className="text-[color:var(--color-emerald)] text-xs font-bold uppercase tracking-[0.25em] mb-2">{t('catalogue_collection')}</p>
          <h1 
            className="text-3xl sm:text-4xl lg:text-5xl text-heading font-bold"
            style={{ fontFamily: 'Cormorant Garamond, serif' }}
          >
            {t('catalogue_title')}
          </h1>
        </div>

        {/* Top Search Bar */}
        <form onSubmit={handleSearch} className="max-w-2xl mb-5">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted" />
            <input
              type="text"
              placeholder={t('catalogue_search_placeholder')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full input-field pl-12 pr-28 py-3.5 text-base rounded-full shadow-sm"
              data-testid="search-bar"
            />
            <button
              type="submit"
              className="absolute right-1.5 top-1/2 -translate-y-1/2 bg-[color:var(--color-primary)] text-white rounded-full px-5 py-2 text-sm font-bold hover:bg-[color:var(--color-primary-hover)] transition-colors"
              data-testid="search-submit"
            >
              {t('catalogue_search_btn')}
            </button>
          </div>
        </form>

        {/* Chips de catégories */}
        <div className="flex gap-2 overflow-x-auto no-scrollbar pb-3 mb-5 -mx-4 px-4 sm:mx-0 sm:px-0" data-testid="category-chips">
          <button type="button" onClick={() => setCategory('')} className={`chip ${!category ? 'chip-active' : ''}`} data-testid="chip-category-all">{t('catalogue_all')}</button>
          {categories.map(cat => (
            <button key={cat} type="button" onClick={() => setCategory(cat)} className={`chip ${category === cat ? 'chip-active' : ''}`} data-testid={`chip-category-${cat}`}>{cat}</button>
          ))}
        </div>

        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar Filters */}
          <aside className={`lg:w-64 flex-shrink-0 ${showFilters ? 'block' : 'hidden lg:block'}`}>
            <div className="card-surface rounded-2xl p-5 sticky top-24 max-h-[calc(100vh-8rem)] overflow-y-auto">
              <div className="flex items-center justify-between mb-5">
                <h3 className="text-heading text-sm font-bold uppercase tracking-widest" style={{ fontFamily: 'Manrope, sans-serif' }}>{t('catalogue_filters')}</h3>
                {hasActiveFilters && (
                  <button
                    onClick={clearFilters}
                    className="text-xs text-muted hover:text-[color:var(--color-primary)] transition-colors"
                    data-testid="clear-filters"
                  >
                    {t('catalogue_clear')}
                  </button>
                )}
              </div>

              {/* Categories */}
              <div className="mb-5">
                <h4 className="text-muted text-xs uppercase tracking-widest mb-3">{t('catalogue_categories')}</h4>
                <div className="space-y-1">
                  <button
                    onClick={() => setCategory('')}
                    className={`block w-full text-left px-3 py-2 text-sm transition-colors ${
                      !category ? 'bg-[color:var(--color-primary)] text-white font-semibold rounded-xl' : 'text-body hover:text-heading hover:bg-sand rounded-xl'
                    }`}
                    data-testid="category-all"
                  >
                    {t('catalogue_all')}
                  </button>
                  {categories.map(cat => (
                    <button
                      key={cat}
                      onClick={() => setCategory(cat)}
                      className={`block w-full text-left px-3 py-2 text-sm transition-colors ${
                        category === cat ? 'bg-[color:var(--color-primary)] text-white font-semibold rounded-xl' : 'text-body hover:text-heading hover:bg-sand rounded-xl'
                      }`}
                      data-testid={`category-${cat}`}
                    >
                      {cat}
                    </button>
                  ))}
                </div>
              </div>

              {/* Brands */}
              <div className="mb-5">
                <h4 className="text-muted text-xs uppercase tracking-widest mb-3">{t('catalogue_brands')}</h4>
                <div className="space-y-1 max-h-48 overflow-y-auto">
                  <button
                    onClick={() => setBrand('')}
                    className={`block w-full text-left px-3 py-2 text-sm transition-colors ${
                      !brand ? 'bg-[color:var(--color-primary)] text-white font-semibold rounded-xl' : 'text-body hover:text-heading hover:bg-sand rounded-xl'
                    }`}
                    data-testid="brand-all"
                  >
                    {t('catalogue_all')}
                  </button>
                  {brands.map(b => (
                    <button
                      key={b}
                      onClick={() => setBrand(b)}
                      className={`block w-full text-left px-3 py-2 text-sm transition-colors ${
                        brand === b ? 'bg-[color:var(--color-primary)] text-white font-semibold rounded-xl' : 'text-body hover:text-heading hover:bg-sand rounded-xl'
                      }`}
                      data-testid={`brand-${b}`}
                    >
                      {b}
                    </button>
                  ))}
                </div>
              </div>

              {/* Price Range */}
              <div className="mb-5">
                <h4 className="text-muted text-xs uppercase tracking-widest mb-3">{t('catalogue_price')}</h4>
                <div className="flex gap-2">
                  <input
                    type="number"
                    placeholder="Min"
                    value={minPrice}
                    onChange={(e) => setMinPrice(e.target.value)}
                    className="w-1/2 input-field text-sm"
                    data-testid="filter-min-price"
                  />
                  <input
                    type="number"
                    placeholder="Max"
                    value={maxPrice}
                    onChange={(e) => setMaxPrice(e.target.value)}
                    className="w-1/2 input-field text-sm"
                    data-testid="filter-max-price"
                  />
                </div>
              </div>

              {/* In Stock */}
              <label className="flex items-center gap-3 cursor-pointer mb-5">
                <input
                  type="checkbox"
                  checked={inStock}
                  onChange={(e) => setInStock(e.target.checked)}
                  className="w-4 h-4 accent-[color:var(--color-primary)]"
                  data-testid="filter-in-stock"
                />
                <span className="text-body text-sm">{t('catalogue_in_stock_only')}</span>
              </label>

              {/* Promos */}
              <label className="flex items-center gap-3 cursor-pointer mb-5">
                <input
                  type="checkbox"
                  checked={promoOnly}
                  onChange={(e) => { setPromoOnly(e.target.checked); const sp = new URLSearchParams(searchParams); e.target.checked ? sp.set('promo', '1') : sp.delete('promo'); setSearchParams(sp, { replace: true }); }}
                  className="w-4 h-4 accent-red-500"
                  data-testid="filter-promo"
                />
                <span className="text-red-400 text-sm">{t('catalogue_promo_only')}</span>
              </label>

              {/* Sort */}
              <div>
                <h4 className="text-muted text-xs uppercase tracking-widest mb-3">{t('catalogue_sort_by')}</h4>
                <select
                  value={`${sortBy}-${sortOrder}`}
                  onChange={(e) => {
                    const [by, order] = e.target.value.split('-');
                    setSortBy(by);
                    setSortOrder(order);
                  }}
                  className="w-full input-field text-sm"
                  data-testid="filter-sort"
                >
                  <option value="created_at-desc">{t('catalogue_sort_newest')}</option>
                  <option value="created_at-asc">{t('catalogue_sort_oldest')}</option>
                  <option value="price_detail-asc">{t('catalogue_sort_price_asc')}</option>
                  <option value="price_detail-desc">{t('catalogue_sort_price_desc')}</option>
                  <option value="name-asc">{t('catalogue_sort_name_asc')}</option>
                  <option value="name-desc">{t('catalogue_sort_name_desc')}</option>
                </select>
              </div>
            </div>
          </aside>

          {/* Main Content */}
          <div className="flex-1">
            {/* Toolbar */}
            <div className="flex items-center justify-between mb-6">
              <p className="text-muted text-sm">
                {t('catalogue_products_count', { count: products.length, plural: products.length !== 1 ? 's' : '' })}
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className="lg:hidden p-2 border border-theme hover:border-[color:rgb(var(--primary-rgb)/0.30)] transition-colors"
                  data-testid="toggle-filters"
                >
                  <SlidersHorizontal className="w-4 h-4 text-muted" />
                </button>
                <button
                  onClick={() => setCompactView(false)}
                  className={`p-2 border transition-colors ${!compactView ? 'border-[color:var(--color-primary)] text-[color:var(--color-primary)]' : 'border-theme text-muted hover:border-[color:rgb(var(--primary-rgb)/0.30)]'}`}
                  data-testid="view-large"
                >
                  <LayoutGrid className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setCompactView(true)}
                  className={`p-2 border transition-colors ${compactView ? 'border-[color:var(--color-primary)] text-[color:var(--color-primary)]' : 'border-theme text-muted hover:border-[color:rgb(var(--primary-rgb)/0.30)]'}`}
                  data-testid="view-compact"
                >
                  <Grid3X3 className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Products Grid */}
            {loading ? (
              <div className={`grid gap-4 ${compactView ? 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-4' : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3'}`}>
                {[...Array(12)].map((_, i) => (
                  <div key={`skeleton-${i}`} className="card-surface animate-pulse">
                    <div className={compactView ? 'aspect-square bg-surface' : 'aspect-[3/4] bg-surface'} />
                    <div className="p-3 space-y-2">
                      <div className="h-2 bg-surface w-1/3" />
                      <div className="h-3 bg-surface w-2/3" />
                      <div className="h-3 bg-surface w-1/2" />
                    </div>
                  </div>
                ))}
              </div>
            ) : products.length > 0 ? (
              <div 
                className={`grid gap-4 ${compactView ? 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-4' : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3'}`}
                data-testid="products-grid"
              >
                {products.map(product => (
                  <ProductCard key={product.id} product={product} compact={compactView} />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <p className="text-muted text-lg mb-4">{t('catalogue_no_results')}</p>
                <button
                  onClick={clearFilters}
                  className="text-[color:var(--color-primary)] hover:underline text-sm"
                  data-testid="no-results-clear"
                >
                  {t('catalogue_clear_filters')}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CataloguePage;
